import PresentableError from "../../errors/PresentableError";
import RequestError from "../../errors/RequestError";
import RequestTimeoutError from "../../errors/RequestTimeoutError";
import ParseableError from "../../errors/ParseableError";
import DataBaseNativeError from "../../errors/DataBaseNativeError";
import RequestAbortedError from "../../errors/RequestAbortedError";
import AuthenticationError from "../../errors/AuthenticationError";
import PayloadTooLargeError from "../../errors/PayloadTooLargeError";
import ModifiedInConcurrentSessionError from "../../errors/ModifiedInConcurrentSessionError";

export default function errorHandlerService() {
    // API
    //
    return {
        rethrowAsInterpretableDomainError: rethrowAsInterpretableDomainError,
    };

    /**
     * Handles general errors by re-throwing them as understandable errors, e.g. PresentableError.
     *
     * @param {Error} error
     * @throws {PresentableError}
     */

    async function rethrowAsInterpretableDomainError(error, xhrRequest) {
        // re-throw if this is already a presentable error
        //
        if (error instanceof PresentableError) {
            throw error;
        }

        // transform XHR aborted or timed out errors and re-throw
        //
        if (error && error.type === "abort") {
            throw new RequestAbortedError(xhrRequest);
        }

        if (error && error.type === "timeout") {
            throw new RequestTimeoutError(xhrRequest);
        }

        // throw authentication error on 401 Unauthorized
        //
        if (Number.parseInt(xhrRequest.status, 10) === 401) {
            throw new AuthenticationError();
        }

        if (error.responseType === "blob") {
            if (Number.parseInt(xhrRequest.status, 10) === 413) {
                throw new PayloadTooLargeError();
            }
            if (Number.parseInt(xhrRequest.status, 10) === 504) {
                throw new RequestTimeoutError(xhrRequest);
            }

            if (error.response.type === "application/json") {
                const jsonErrorString = await error.response.text();
                throw _parseError(xhrRequest, jsonErrorString);
            }
        }

        // transform HANA error and re-throw
        //
        if (DataBaseNativeError.check(xhrRequest)) {
            throw new DataBaseNativeError(xhrRequest);
        }

        if (ParseableError.isRequestParseable(xhrRequest)) {
            throw _parseError(xhrRequest, xhrRequest.response);
        }

        throw new RequestError(xhrRequest);
    }

    /**
     * Classifies a parseable API error response, so every endpoint returning
     * the "modified in concurrent session" error is handled the same way -
     * callers no longer need to detect this error type themselves, they can
     * just funnel it into $sbErrorPresenter.catch like any other error.
     *
     * @param {XMLHttpRequest} xhrRequest
     * @param {string} response - the raw JSON response body used to determine
     *   the error code. Kept separate from xhrRequest.response, which for
     *   blob requests holds the original binary payload, not this text.
     * @returns {ParseableError|ModifiedInConcurrentSessionError}
     * @private
     */
    function _parseError(xhrRequest, response) {
        var code = ParseableError.parseResponse(response);

        if (code === ModifiedInConcurrentSessionError.CODE) {
            var responseParsed = JSON.parse(response);
            return new ModifiedInConcurrentSessionError(responseParsed.message);
        }

        return new ParseableError(xhrRequest, code);
    }
}
